/* eslint-disable react/prop-types */
// Single source of truth for the results grid columns, shared by the collapsed
// program row and the expanded rate stack. Both render rate shaped data: the
// program row uses the target rate that calcDeltas hoists onto the program.
import { useCallback, useMemo } from 'react';
import Grid from '@elliemae/ds-grid';
import { DSIconColors } from '@elliemae/ds-icon';
import { AlertsDetail } from '@elliemae/ds-icons';
import { DSTooltipV2 } from '@elliemae/ds-tooltip';
import {
  dispApr,
  dispCurrencyMask,
  dispDti,
  dispPrice,
  dispRate,
  shouldDisplayAPR,
  getLockIconDisplay,
  shouldShowGetMIColumn,
  toNumber,
  NON_CONVENTIONAL_PRODUCT_TYPES,
} from '../../helpers';
import {
  GetMIButton,
  MI_ALREADY_ORDERED_TOOLTIP,
  MI_NOT_APPLICABLE_TOOLTIP,
  getMIKey,
  getMIDropdownOptions,
  isMIFirstPhaseLoading,
  isMILoading,
  isMIError,
  MILoadingSkeleton,
  useMIContext,
  useMIRowState,
} from '../GetMIButton';
import type { MiType } from '../GetMIButton';
import Session, { IS_DEBUG } from '../../../../common/services/Session';
import {
  StyledGridDetailsLink,
  StyledGridLink,
  StyledIconThenValue,
} from './styled';

export type ResultsColumnId =
  | 'rate'
  | 'price'
  | 'priceVal'
  | 'pnI'
  | 'mi'
  | 'getMI'
  | 'apr'
  | 'bRatio'
  | 'details';

/** Fields the grid renders. Satisfied by RateDetails and by LoanProgram. */
export interface RateLike {
  rateDataID: number;
  rate: number | null;
  price: number;
  priceVal: number;
  pnI: number;
  apr: number | null;
  bRatio: number | null;
  lockEffectiveDate?: string;
}

export interface CellContext {
  programID: number;
  productTypeID: number;
  /** full program row, needed by the MI button and the details slide */
  program: Record<string, unknown>;
  displayEstimatedMI?: string;
  relockRequest?: {
    specificProgramID?: number;
    specificRateDataID?: number;
  } | null;
  rateRequestStatus?: number;
  /** MORTGAGE_INSURANCE contact carrying a certification reference number */
  miCertification?: unknown;
  /** MI amount Encompass already holds, used instead of a fetched estimate */
  proposedMIAmount?: number;
  /** Configured MI types from lookups, drives the type-selection modal */
  miTypes?: MiType[];
  onSelectRate: (rate: RateLike) => void;
  onOpenDetails: (rate: RateLike) => void;
}

export interface ResultsColumn {
  id: ResultsColumnId;
  header: string;
  /**
   * grid-template-columns track. Fixed width on purpose: block virtualisation
   * only renders part of the list, so a content sized track would change width
   * as you scroll.
   */
  track: string;
  renderCell: (rate: RateLike, ctx: CellContext) => React.ReactNode;
}

export interface ColumnVisibilityInput {
  clientSettings?: unknown;
  ltv?: number;
  standardProducts?: number[];
  loanTerms?: unknown[];
  loanTermsLookup?: unknown[];
  loanChannel?: number;
  /** Widens the APR column for the "Use Encompass" text. */
  aprIsText?: boolean;
}

// Marks the one rate in the stack that is actually locked. The program cell
// icon matches on programID alone, this one needs the rate too.
const RateCell = ({ rate, ctx }: { rate: RateLike; ctx: CellContext }) => {
  const isLockedRate =
    ctx.relockRequest?.specificRateDataID === rate.rateDataID &&
    ctx.relockRequest?.specificProgramID === ctx.programID;
  const { LockIcon, color, title } = getLockIconDisplay(ctx.rateRequestStatus);
  return (
    <StyledIconThenValue>
      {isLockedRate && <LockIcon color={color} title={title} />}
      <StyledGridLink
        aria-label={`Select rate ${dispRate(rate.rate, rate.priceVal)}`}
        onClick={() => ctx.onSelectRate(rate)}
      >
        {dispRate(rate.rate, rate.priceVal)}
      </StyledGridLink>
    </StyledIconThenValue>
  );
};

const stalePricingTooltip = (rateDate: string) => {
  const when = new Date(rateDate);
  const date = when.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const time = when.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `Pricing for this product was last updated at ${time} EST on ${date}`;
};

const RightAligned = ({ children }: { children: React.ReactNode }) => (
  <Grid width="100%" justifyContent="flex-end" alignItems="center">
    {children}
  </Grid>
);

// Once Encompass holds an MI certification for the loan, its amount replaces
// anything we would fetch, and there is nothing left to wait on.
const useMIAmount = (rate: RateLike, ctx: CellContext) => {
  const { status, miAmount: storedMIAmount } = useMIRowState(
    getMIKey(rate.rateDataID, ctx.programID),
  );
  const isMIEligible = !NON_CONVENTIONAL_PRODUCT_TYPES.includes(
    ctx.productTypeID,
  );
  return {
    isMIEligible,
    miAmount:
      isMIEligible && ctx.miCertification
        ? ctx.proposedMIAmount
        : storedMIAmount,
    isLoading: isMIFirstPhaseLoading(status) && !ctx.miCertification,
  };
};

const MiAmountCell = ({ rate, ctx }: { rate: RateLike; ctx: CellContext }) => {
  const { miAmount, isLoading } = useMIAmount(rate, ctx);
  if (isLoading) return <MILoadingSkeleton />;
  return miAmount != null ? (
    <RightAligned>{dispCurrencyMask(miAmount)}</RightAligned>
  ) : null;
};

const PmtCell = ({ rate, ctx }: { rate: RateLike; ctx: CellContext }) => {
  const { miAmount, isLoading } = useMIAmount(rate, ctx);
  if (isLoading) return <MILoadingSkeleton />;
  // PMT is principal and interest plus any MI amount.
  return (
    <RightAligned>
      {dispCurrencyMask(toNumber(rate.pnI) + (miAmount ?? 0))}
    </RightAligned>
  );
};

const AprCell = ({ rate, ctx }: { rate: RateLike; ctx: CellContext }) => {
  const { status, aprValue } = useMIRowState(
    getMIKey(rate.rateDataID, ctx.programID),
  );
  if (isMILoading(status))
    return <MILoadingSkeleton hasEncApr={rate?.apr === 0} />;
  const value = dispApr(aprValue != null ? aprValue : rate.apr);
  return <RightAligned>{value}</RightAligned>;
};

const GetMiCell = ({ rate, ctx }: { rate: RateLike; ctx: CellContext }) => {
  const { handleGetMI } = useMIContext();
  const { isMIEligible, isLoading } = useMIAmount(rate, ctx);
  const { status, miError } = useMIRowState(
    getMIKey(rate.rateDataID, ctx.programID),
  );
  const isError = isMIError(status);
  const isDebug = Session.get(IS_DEBUG);
  const miTypes = ctx.miTypes ?? [];
  const dropdownOptions = useMemo(
    () => getMIDropdownOptions(miTypes),
    [miTypes],
  );
  const onSelectMiType = useCallback(
    (value: string) =>
      handleGetMI?.({
        rateDataID: rate.rateDataID,
        programID: ctx.programID,
        rateData: ctx.program,
        displayEstimatedMI: ctx.displayEstimatedMI,
        selectedMiType: value,
      }),
    [
      handleGetMI,
      rate.rateDataID,
      ctx.programID,
      ctx.program,
      ctx.displayEstimatedMI,
    ],
  );

  if (isLoading) return <MILoadingSkeleton />;

  const isDisabled = isDebug || !isMIEligible || !!ctx.miCertification;
  const disabledTooltip =
    ctx.miCertification && isMIEligible
      ? MI_ALREADY_ORDERED_TOOLTIP
      : MI_NOT_APPLICABLE_TOOLTIP;

  const sharedButtonProps = {
    isDisabled,
    disabledTooltip,
    isError,
    errorTooltip: miError?.tooltipMessage,
    isLoading: false,
  };

  return (
    <RightAligned>
      <GetMIButton
        {...sharedButtonProps}
        {...(miTypes.length <= 1
          ? {
              onClick: () =>
                handleGetMI?.({
                  rateDataID: rate.rateDataID,
                  programID: ctx.programID,
                  rateData: ctx.program,
                  displayEstimatedMI: ctx.displayEstimatedMI,
                  selectedMiType: miTypes[0]?.miType ?? '',
                }),
            }
          : { options: dropdownOptions, onSelectMiType })}
      />
    </RightAligned>
  );
};

// APR holds either a number or the words "Use Encompass". That text measures
// 92px in the grid font and cells carry 8px of padding each side, so the wide
// track needs 108px to stay on one line. It is only used when the data actually
// carries the text, so clients with real APR values keep the narrow track.
// Both are fixed widths: a content sized track would resize as the user
// scrolls, since only part of the list is ever in the DOM.
export const APR_TRACK_NUMERIC = '64px';
export const APR_TRACK_TEXT = '112px';

/**
 * True when any rate has no APR, which `dispApr` renders as "Use Encompass".
 * @param programs the whole result set, not the filtered view, so the filter
 * pills cannot resize the column
 * @returns whether the wide APR track is needed
 */
export const hasEncompassApr = (
  programs: { apr?: number; rateDetails?: { apr?: number }[] }[] = [],
): boolean =>
  programs.some(
    (p) => p.apr === 0 || (p.rateDetails ?? []).some((r) => r.apr === 0),
  );

const ALL_COLUMNS: ResultsColumn[] = [
  {
    id: 'rate',
    header: 'Rate',
    track: '88px',
    renderCell: (rate, ctx) => <RateCell rate={rate} ctx={ctx} />,
  },
  {
    id: 'price',
    header: 'Price',
    track: '76px',
    renderCell: (rate) => (
      <RightAligned>{dispPrice(rate.price, rate.priceVal)}</RightAligned>
    ),
  },
  {
    id: 'priceVal',
    header: 'Price ($)',
    track: '80px',
    renderCell: (rate) => (
      <RightAligned>{dispCurrencyMask(rate.priceVal)}</RightAligned>
    ),
  },
  {
    id: 'pnI',
    header: 'PMT',
    track: '76px',
    renderCell: (rate, ctx) => <PmtCell rate={rate} ctx={ctx} />,
  },
  {
    id: 'mi',
    header: 'MI PMT',
    track: '88px',
    renderCell: (rate, ctx) => <MiAmountCell rate={rate} ctx={ctx} />,
  },
  {
    id: 'getMI',
    header: 'GET MI',
    track: '88px',
    renderCell: (rate, ctx) => <GetMiCell rate={rate} ctx={ctx} />,
  },
  {
    id: 'apr',
    header: 'APR',
    track: APR_TRACK_NUMERIC,
    renderCell: (rate, ctx) => <AprCell rate={rate} ctx={ctx} />,
  },
  {
    id: 'bRatio',
    header: 'DTI',
    track: '56px',
    renderCell: (rate) => <RightAligned>{dispDti(rate.bRatio)}</RightAligned>,
  },
  {
    id: 'details',
    header: 'QM/Det',
    track: '88px',
    renderCell: (rate, ctx) => {
      // No link when the rate has no price.
      const hasPrice = rate.price !== undefined && rate.price !== null;
      if (!hasPrice) return <RightAligned>--</RightAligned>;
      // Stale here is rateStale alone, unlike the badge on the program cell
      // which also weighs rateDocMaxAge and historical pricing.
      const isStale = ctx.program.rateStale === 1;
      return (
        <StyledIconThenValue>
          {isStale && (
            <DSTooltipV2
              title={stalePricingTooltip(ctx.program.rateDate as string)}
              placement="top"
              triggerComponent={
                <AlertsDetail size="s" color={DSIconColors.DANGER} />
              }
            />
          )}
          <StyledGridDetailsLink
            aria-label={`Details for ${ctx.program.program} at ${dispRate(
              rate.rate,
              rate.priceVal,
            )}`}
            onClick={() => ctx.onOpenDetails(rate)}
          >
            details
          </StyledGridDetailsLink>
        </StyledIconThenValue>
      );
    },
  },
];

/** Program column minimum before its text wraps. */
export const PROGRAM_COLUMN_MIN = '320px';

/**
 * Visible columns in display order.
 * @param input client settings and loan values driving conditional columns
 * @returns visible columns
 */
export const getResultsColumns = (
  input: ColumnVisibilityInput,
): ResultsColumn[] => {
  const showMI = shouldShowGetMIColumn(input);
  const showAPR = shouldDisplayAPR(input.clientSettings);
  return ALL_COLUMNS.filter((col) => {
    if (col.id === 'mi' || col.id === 'getMI') return Boolean(showMI);
    if (col.id === 'apr') return Boolean(showAPR);
    return true;
  }).map((col) =>
    col.id === 'apr' && input.aprIsText
      ? { ...col, track: APR_TRACK_TEXT }
      : col,
  );
};

/**
 * Template shared by header, program rows and rate rows so all stay aligned.
 * @param columns visible rate columns
 * @returns grid-template-columns value
 */
export const getGridTemplateColumns = (columns: ResultsColumn[]): string =>
  [
    // Rate columns are fixed and the program column takes what is left, so
    // hiding a column widens the program cell rather than the rates.
    `minmax(${PROGRAM_COLUMN_MIN}, 1fr)`,
    ...columns.map((c) => c.track),
  ].join(' ');
